import { useCallback } from 'react';
import { errorHandler, type AppError, createError } from '@/lib/errorHandler';

export interface UseErrorHandlerReturn {
  handleError: (error: unknown, context?: string) => AppError;
  handleAsyncError: (asyncFn: () => Promise<any>, context?: string) => Promise<any>;
  createNetworkError: (message: string, context?: Record<string, any>) => AppError;
  createAuthError: (message: string, context?: Record<string, any>) => AppError;
  createValidationError: (message: string, context?: Record<string, any>) => AppError;
  createPermissionError: (message: string, context?: Record<string, any>) => AppError;
  createSystemError: (message: string, context?: Record<string, any>) => AppError;
}

export const useErrorHandler = (defaultContext?: string): UseErrorHandlerReturn => {
  const handleError = useCallback((error: unknown, context?: string): AppError => {
    const finalContext = context || defaultContext;
    return errorHandler.handle(error, finalContext);
  }, [defaultContext]);

  const handleAsyncError = useCallback(async (
    asyncFn: () => Promise<any>, 
    context?: string
  ): Promise<any> => {
    try {
      return await asyncFn();
    } catch (error) {
      throw handleError(error, context || defaultContext);
    }
  }, [handleError, defaultContext]);

  const createNetworkError = useCallback((message: string, context?: Record<string, any>) => {
    return createError.network(message, context);
  }, []);

  const createAuthError = useCallback((message: string, context?: Record<string, any>) => {
    return createError.auth(message, context);
  }, []);

  const createValidationError = useCallback((message: string, context?: Record<string, any>) => {
    return createError.validation(message, context);
  }, []);

  const createPermissionError = useCallback((message: string, context?: Record<string, any>) => {
    return createError.permission(message, context);
  }, []);

  const createSystemError = useCallback((message: string, context?: Record<string, any>) => {
    return createError.system(message, context);
  }, []);

  return {
    handleError,
    handleAsyncError,
    createNetworkError,
    createAuthError,
    createValidationError,
    createPermissionError,
    createSystemError
  };
};